import { NavLink, useLocation } from 'react-router';
import { cn, VisuallyHidden } from '@ircf/ui';
import type { RouteDefinition } from '@ircf/config';

type ShellVariant = 'client' | 'trainer';

interface PrimaryItem {
  id: string;
  label: string;
  icon: (props: { className?: string }) => JSX.Element;
}

const CLIENT_PRIMARY: readonly PrimaryItem[] = [
  { id: 'portal-dashboard', label: 'Home', icon: HomeIcon },
  { id: 'portal-training', label: 'Train', icon: TrainIcon },
  { id: 'portal-nutrition', label: 'Fuel', icon: FuelIcon },
  { id: 'portal-progress', label: 'Progress', icon: ChartIcon },
];

const TRAINER_PRIMARY: readonly PrimaryItem[] = [
  { id: 'admin-dashboard', label: 'Home', icon: HomeIcon },
  { id: 'admin-clients', label: 'Clients', icon: PeopleIcon },
  { id: 'admin-plans-training', label: 'Plans', icon: PlansIcon },
  { id: 'admin-check-ins', label: 'Check-ins', icon: CheckIcon },
];

export function primaryRouteIds(variant: ShellVariant): readonly string[] {
  return (variant === 'client' ? CLIENT_PRIMARY : TRAINER_PRIMARY).map((item) => item.id);
}

export function PortalBottomNav({
  variant,
  nav,
  badges,
  moreOpen,
  onToggleMore,
  moreId,
}: {
  variant: ShellVariant;
  nav: readonly RouteDefinition[];
  badges: Partial<Record<string, number>>;
  moreOpen: boolean;
  onToggleMore: () => void;
  moreId: string;
}) {
  const items = variant === 'client' ? CLIENT_PRIMARY : TRAINER_PRIMARY;
  const byId = new Map(nav.map((route) => [route.id, route]));
  const primaryIds = new Set(items.map((item) => item.id));
  const location = useLocation();
  const onSecondary = nav.some((route) => {
    if (primaryIds.has(route.id) || !route.inNav) return false;
    return (
      location.pathname === route.path || location.pathname.startsWith(`${route.path}/`)
    );
  });
  const moreCount = nav.reduce((total, route) => {
    if (primaryIds.has(route.id) || !route.inNav) return total;
    return total + (badges[route.id.replace(/^(portal|admin)-/, '')] ?? 0);
  }, 0);

  return (
    <nav
      aria-label="Primary"
      data-print-hide="true"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-near-black/90 pb-[env(safe-area-inset-bottom,0px)] backdrop-blur-xl lg:hidden"
    >
      <ul className="grid h-[4.25rem] grid-cols-5">
        {items.map((item) => {
          const route = byId.get(item.id);
          if (!route) return null;
          const badgeKey = route.id.replace(/^(portal|admin)-/, '');
          const count = badges[badgeKey] ?? 0;
          const Icon = item.icon;

          return (
            <li key={item.id}>
              <NavLink
                to={route.path}
                className={({ isActive }) =>
                  cn(
                    'flex h-full flex-col items-center justify-center gap-0.5 text-[0.65rem] font-medium tracking-wide',
                    'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-warm-white',
                    isActive ? 'text-performance-green' : 'text-cool-steel',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative">
                      <Icon className="size-5" />
                      {count > 0 ? (
                        <span
                          aria-hidden="true"
                          className="absolute -top-1.5 -right-2 flex min-w-3.5 items-center justify-center rounded-full bg-performance-green px-0.5 text-[0.55rem] font-semibold text-near-black"
                        >
                          {count > 9 ? '9+' : count}
                        </span>
                      ) : null}
                    </span>
                    <span className={isActive ? 'text-warm-white' : undefined}>{item.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            aria-expanded={moreOpen}
            aria-controls={moreId}
            onClick={onToggleMore}
            className={cn(
              'flex h-full w-full flex-col items-center justify-center gap-0.5 text-[0.65rem] font-medium tracking-wide',
              'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-warm-white',
              moreOpen || onSecondary ? 'text-performance-green' : 'text-cool-steel',
            )}
          >
            <span className="relative">
              <MoreIcon className="size-5" />
              {moreCount > 0 ? (
                <span aria-hidden="true" className="absolute -top-1.5 -right-2 flex min-w-3.5 items-center justify-center rounded-full bg-performance-green px-0.5 text-[0.55rem] font-semibold text-near-black">
                  {moreCount > 9 ? '9+' : moreCount}
                </span>
              ) : null}
            </span>
            <span className={moreOpen || onSecondary ? 'text-warm-white' : undefined}>More</span>
            {moreCount > 0 ? <VisuallyHidden>{moreCount} items need attention</VisuallyHidden> : null}
            <VisuallyHidden>{moreOpen ? 'Close more navigation' : 'Open more navigation'}</VisuallyHidden>
          </button>
        </li>
      </ul>
    </nav>
  );
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function TrainIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <path d="M6.5 8h11M8 8v8m8-8v8M5 12h14M7 16h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function FuelIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <path d="M4 9.5 12 4l8 5.5V20H4V9.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <path d="M4 19h16M7 16v-5m5 5V8m5 8V5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PeopleIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 19c.6-3 2.6-4.5 5-4.5s4.4 1.5 5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="17" cy="9" r="2.2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M16 14.5c1.8.3 3.2 1.5 3.8 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PlansIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 12.5 10.5 15 16 9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
      <circle cx="6" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="18" cy="12" r="1.4" fill="currentColor" />
    </svg>
  );
}
